// assets/AllCourses/Exercises/ListeningExercise.jsx
"use client";

import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardDescription,
} from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { StringField } from "@/components/shared/FormFields";
import SpeakerButton from "@/components/ui/SpeakerButton";
import { cn } from "@/utils/utils";

export default function ListeningExercise({
  exercise = {},
  onUpdate,            // Edit mode only
  errors = {},
  isPreview = false,   // false = admin edit, true = student view
  showResult = false,
  onComplete = () => {},
  setAttempt = () => {},
}) {
  const isEditMode = typeof onUpdate === "function";

  /* ================= SAFE QUESTIONS ================= */
  const questions = Array.isArray(exercise.listeningQuestions)
    ? exercise.listeningQuestions.map((q) => ({
        wordListening:
          typeof q.wordListening === "string"
            ? q.wordListening.trim()
            : "",
      }))
    : [];

  /* ================= STUDENT STATE ================= */
  const [state, setState] = useState({
    answers: {}, // { [qIndex]: string }
    hasInteracted: false,
  });

  const completedRef = useRef(false);

  /* ================= RESTORE ATTEMPT ================= */
  useEffect(() => {
    if (!isPreview || !exercise.attempt) return;
    setState({
      answers:
        typeof exercise.attempt.answers === "object"
          ? exercise.attempt.answers
          : {},
      hasInteracted: !!exercise.attempt.hasInteracted,
    });
  }, [isPreview, exercise.attempt]);

  /* ================= RESET ON CHANGE ================= */
  useEffect(() => {
    if (isPreview) completedRef.current = false;
  }, [exercise._id, isPreview]);

  /* ================= NORMALIZE ================= */
  const normalize = (text = "") =>
    String(text || "")
      .trim()
      .toLowerCase()
      .replace(/[.,!?]/g, "")
      .replace(/\s+/g, " ");

  /* ================= EDIT MODE HELPERS ================= */
  const updateQuestions = (next) => {
    if (isEditMode) onUpdate("listeningQuestions", next);
  };

  const handleWordChange = (qIdx, value) => {
    if (!isEditMode) return;
    const next = [...questions];
    next[qIdx] = {
      wordListening:
        typeof value === "string" ? value.trim() : value,
    };
    updateQuestions(next);
  };

  const handleAddQuestion = () => {
    if (!isEditMode) return;
    updateQuestions([...questions, { wordListening: "" }]);
  };

  const handleRemoveQuestion = (qIdx) => {
    if (!isEditMode || questions.length === 1) return;
    updateQuestions(questions.filter((_, i) => i !== qIdx));
  };

  /* ================= STUDENT MODE ================= */
  const handleInputChange = (qIdx, value) => {
    if (isEditMode || showResult || completedRef.current) return;

    setState((prev) => {
      const answers = {
        ...prev.answers,
        [qIdx]: value,
      };

      const next = {
        ...prev,
        answers,
        hasInteracted: true,
      };

      setAttempt(next);

      const allAnswered = questions.every(
        (_, i) => answers[i]?.trim()
      );

      if (allAnswered && !completedRef.current) {
        completedRef.current = true;

        const allCorrect = questions.every((q, i) => {
          const user = normalize(answers[i]);
          const correct = normalize(q.wordListening);
          return user === correct;
        });

        onComplete(exercise._id, allCorrect);
      }

      return next;
    });
  };

  /* ================= EDIT MODE RENDER ================= */
  if (isEditMode) {
    return (
      <div className="space-y-8">
        {questions.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No listening items yet. Click "Add Word / Sentence" to begin.
          </p>
        )}

        {questions.map((q, qIdx) => (
          <Card key={qIdx} className="p-6 space-y-4">
            <StringField
              label={`Word / Sentence ${qIdx + 1}`}
              value={q.wordListening}
              onChange={(v) => handleWordChange(qIdx, v)}
              placeholder="apple / Good morning, how are you?"
              required
              error={
                errors?.[
                  `exercise-${exercise?.index ?? qIdx}-listening-${qIdx}`
                ]
              }
            />

            {q.wordListening && (
              <div className="flex items-center gap-3">
                <SpeakerButton text={q.wordListening} />
                <span className="text-sm text-gray-600">
                  Preview pronunciation
                </span>
              </div>
            )}

            <Button
              variant="destructive"
              size="sm"
              disabled={questions.length === 1}
              onClick={() => handleRemoveQuestion(qIdx)}
            >
              Remove
            </Button>
          </Card>
        ))}

        <Button variant="outline" onClick={handleAddQuestion}>
          Add Word / Sentence
        </Button>
      </div>
    );
  }

  /* ================= STUDENT VIEW ================= */
  if (questions.length === 0) {
    return (
      <Card className="max-w-2xl mx-auto">
        <CardHeader>
          <CardDescription>
            No listening questions provided.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className="max-w-3xl mx-auto">
      <CardHeader>
        <CardDescription>
          Listen carefully and type exactly what you hear.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-8">
        {questions.map((q, qIdx) => {
          const userAnswer = state.answers[qIdx] || "";
          const isCorrect =
            normalize(userAnswer) === normalize(q.wordListening);

          return (
            <div
              key={qIdx}
              className="p-6 bg-muted rounded-lg space-y-4"
            >
              <div className="flex items-center gap-4">
                <SpeakerButton text={q.wordListening} size="lg" />
                <span className="text-sm text-muted-foreground">
                  Click to listen, then type below
                </span>
              </div>

              <Textarea
                rows={3}
                placeholder="Type what you hear..."
                value={userAnswer}
                onChange={(e) =>
                  handleInputChange(qIdx, e.target.value)
                }
                className={cn(
                  "text-base",
                  showResult &&
                    userAnswer &&
                    isCorrect &&
                    "border-green-500 bg-green-50",
                  showResult &&
                    userAnswer &&
                    !isCorrect &&
                    "border-red-500 bg-red-50"
                )}
                disabled={showResult || completedRef.current}
              />

              {showResult && userAnswer && (
                <p
                  className={cn(
                    "text-sm font-medium",
                    isCorrect
                      ? "text-green-700"
                      : "text-red-700"
                  )}
                >
                  {isCorrect
                    ? "Correct"
                    : `Wrong — Correct: "${q.wordListening}"`}
                </p>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
